import { Alert, ElementIntent, Text } from '$components'
import type { SmsCredits } from '$api-client/types.gen'
import { cn } from '$utils'

type SmsCreditsBalanceProps = {
  data?: SmsCredits
  isLoading: boolean
}

type CreditStatus = 'normal' | 'low' | 'zero'

export function SmsCreditsBalance({ data, isLoading }: SmsCreditsBalanceProps) {
  const balance = parseCreditValue(data?.balance)
  const usedThisMonth = parseCreditValue(data?.used_this_month)
  const planAllocationRaw = data?.plan_allocation

  const planAllocation = planAllocationRaw !== null && planAllocationRaw !== undefined && planAllocationRaw !== ''
    ? parseCreditValue(planAllocationRaw)
    : null

  const status = getCreditStatus(balance, planAllocation)

  const usagePercent = planAllocation !== null && planAllocation > 0
    ? Math.min(100, Math.round((usedThisMonth / planAllocation) * 100))
    : 0

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <div className="flex items-baseline justify-between gap-4">
          <Text
            type="s"
            className={cn('font-medium', {
              'text-amber-600': status === 'low',
              'text-red-600': status === 'zero',
            })}
          >
            {isLoading ? '—' : `${balance} credits remaining`}
          </Text>

          <Text
            type="s"
            className="text-gray-500"
          >
            {isLoading
              ? '—'
              : `${usedThisMonth} of ${planAllocation ?? '—'} used this month`}
          </Text>
        </div>

        <div
          className="h-1.5 w-full rounded-full bg-neutral-50 overflow-hidden"
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={usagePercent}
          aria-label="SMS credits used this month"
        >
          <div
            className={cn('h-full rounded-full transition-all', {
              'bg-amber-500': status === 'low',
              'bg-red-500': status === 'zero',
              'bg-indigo-500': status === 'normal',
            })}
            style={{ width: `${usagePercent}%` }}
          />
        </div>

      </div>

      {!isLoading && status === 'low' && (
        <Alert intent={ElementIntent.Warning}>
          <Alert.Heading>
            Credits running low
          </Alert.Heading>
          <Alert.Body>
            Your balance is at or below 25% of your plan allocation. Top up to keep SMS reminders sending.
          </Alert.Body>
        </Alert>
      )}

      {!isLoading && status === 'zero' && (
        <Alert intent={ElementIntent.Danger}>
          <Alert.Heading>
            No credits remaining
          </Alert.Heading>
          <Alert.Body>
            SMS reminders will not send until you top up your credit balance.
          </Alert.Body>
        </Alert>
      )}
    </div>
  )
}

function parseCreditValue(value: string | number | null | undefined): number {
  if (value === null || value === undefined || value === '') {
    return 0
  }

  return Number(value)
}

function getCreditStatus(balance: number, planAllocation: number | null): CreditStatus {
  if (balance <= 0) {
    return 'zero'
  }

  if (planAllocation !== null && planAllocation > 0 && balance <= Math.floor(planAllocation * 0.25)) {
    return 'low'
  }

  return 'normal'
}
